import { Button, Icon, IconName, Text, Tooltip, TooltipContent, TooltipTrigger, cn } from '@flatfile/baseline'
import { FC, ReactNode, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'

export const SideBarLink: FC<{
  label: string
  icon: IconName | undefined
  link?: string
  badge?: string | number
  onClick?: () => void
  action?: string
  active?: boolean
  links?: { label: string; link: string }[]
  disabled?: boolean
  systemIcon?: boolean
  renderCustomIcon?: (_iconName: IconName) => ReactNode
  className?: string
}> = (link) => {
  const nav = useNavigate()
  const { links: sublinks } = link
  const hasLinks = Array.isArray(sublinks)
  const location = useLocation()
  const hasActiveLink = hasLinks && sublinks.some((sublink) => location.pathname === sublink.link)
  const [isExpanded, setIsExpanded] = useState(hasActiveLink)

  const items = (hasLinks ? sublinks : []).map((sublink) => (
    <div
      role='button'
      key={sublink.label}
      onClick={() => nav(sublink.link)}
      className={cn(
        'w-full text-xs px-4 py-2 ml-6 border-l border-gray-300 text-primary hover:bg-interactive-state-selected-enabled text-left',
        location.pathname === sublink.link && 'text-primary font-semibold'
      )}
    >
      {sublink.label}
    </div>
  ))

  // Determine which icon rendering function to use
  let iconElement: ReactNode = null

  if (link.icon) {
    if (link.renderCustomIcon) {
      iconElement = link.renderCustomIcon(link.icon)
    } else {
      iconElement = <Icon name={link.icon} size={16} className='text-primary' />
    }
  }

  // Create button content that will be used in both cases
  const buttonContent = (
    <>
      <div className='flex items-center gap-2'>
        {iconElement}
        <Text variant='body' className='truncate max-w-[150px] text-primary'>
          {link.label}
        </Text>
      </div>

      <div className='flex items-center'>
        {link.badge && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-block cursor-pointer'>
                <Icon size={16} className='ml-1' name='circle-info' color='gray-400' />
              </span>
            </TooltipTrigger>
            <TooltipContent className='flex items-center px-2 py-1'>
              <Text variant='body' className='text-sm'>
                {link.badge}
              </Text>
            </TooltipContent>
          </Tooltip>
        )}

        {link.action && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-block cursor-pointer'>
                <Icon name='circle-info' size={16} className='ml-1' color='gray-400' />
              </span>
            </TooltipTrigger>
            <TooltipContent className='flex items-center px-2 py-1'>
              <Text variant='body' className='text-sm'>
                {link.action}
              </Text>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </>
  )

  const buttonClass = cn(
    'w-[calc(100%+0.75rem)] justify-between items-center p-1.5 -ml-1.5 mr-1.5 hover:bg-surface-sentiment-subtle-neutral',
    location.pathname === link.link && 'text-primary font-medium ',
    link.disabled && 'opacity-30',
    link.className
  )

  const handleClick = () => {
    if (link.link) return
    if (link.onClick) link.onClick()
    if (hasLinks) setIsExpanded(!isExpanded)
  }

  const content = link.link ? (
    <NavLink to={link.link}>
      {({ isActive }) => (
        <Button
          variant='ghost'
          className={cn(buttonClass, isActive && 'text-primary font-medium')}
          onClick={handleClick}
        >
          {buttonContent}
        </Button>
      )}
    </NavLink>
  ) : (
    <Button variant='ghost' className={buttonClass} onClick={handleClick}>
      {buttonContent}
    </Button>
  )

  return (
    <>
      {content}

      {hasLinks && (
        <div
          className={cn('transition-all duration-200 ease-in-out overflow-hidden', isExpanded ? 'max-h-96' : 'max-h-0')}
        >
          {items}
        </div>
      )}
    </>
  )
}
